import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Definindo a interface do Jogo aqui para manter o componente auto-contido
interface Game {
  id: string;
  name: string;
  thumbnail: string;
  provider_name: string;
}

interface ProviderCarouselProps {
  providerName: string;
  games: Game[];
}

export const ProviderCarousel: React.FC<ProviderCarouselProps> = ({ providerName, games }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      // Verifica se pode rolar para a esquerda
      setCanScrollLeft(container.scrollLeft > 0);
      // Verifica se pode rolar para a direita
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      setCanScrollRight(container.scrollLeft < maxScrollLeft - 1); // -1 para tolerância
    }
  };

  const handleScroll = (scrollOffset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: scrollOffset, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Verificação inicial
      checkScroll();
      // Adiciona listeners para scroll e resize
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      // Limpa os listeners quando o componente é desmontado
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [games]); // Re-avalia quando os jogos mudam

  return (
    <div className="mb-12 relative overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-white-600">{providerName}</h2>
      </div>
      <div className="absolute top-1/2 -left-5 -translate-y-1/2 z-10 hidden md:block">
        {canScrollLeft && (
            <button onClick={() => handleScroll(-300)} className="p-2 rounded-full bg-primary hover:bg-secondary text-white transition-all duration-300 shadow-lg">
                <ChevronLeft size={24} />
            </button>
        )}
      </div>
      <div className="absolute top-1/2 -right-5 -translate-y-1/2 z-10 hidden md:block">
        {canScrollRight && (
            <button onClick={() => handleScroll(300)} className="p-2 rounded-full bg-primary hover:bg-secondary text-white transition-all duration-300 shadow-lg">
                <ChevronRight size={24} />
            </button>
        )}
      </div>
      <div ref={scrollContainerRef} className="flex overflow-x-auto space-x-4 pb-4 no-scrollbar">
        {games.map(game => (
          <Link to={`/game/${game.id}`} key={game.id} className="block group flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer w-40">
              <img src={game.thumbnail} alt={game.name} className="w-full h-24 object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="p-2">
                <h3 className="font-semibold text-gray-800 text-xs truncate">{game.name}</h3>
                <p className="text-gray-500 text-xs">{game.provider_name}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};